
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from "react-router-dom"

import { detailsActions } from '../../../../store/details';
import { workqueueActions } from '../../../../store/workqueue';
import { getWorkQDrwaer } from '../../../../lib/fetchApi';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { caseActions } from '../../../../store/case';
import { socket } from '../../../../socket';
import { applyDeletedNotesPatch } from '../../../../lib/applyNoteSoftDelete';
import {
    getCaseIdFromCasePayload,
    mergeCaseUpdateData,
    sanitizeCaseUpdatePayload,
    normalizeActiveCaseShape,
    recalculateCaseCounters,
} from '../../../../lib/caseUpdateHelpers';
import { WorkQueueUpdateContext } from './WorkQueueUpdateContext';
import { tabs_button } from '../../../../assets/data';
import { viewTabPermission } from '../../../../lib/permissions';
import {
    computeVisiblePatientTabItems,
    drawerTabMatchesPatientVisibility,
    DRAWER_TAB_TO_PATIENT_TAB,
    getCaseTypeTabConfig,
} from '../../../../lib/patientTabVisibility';
import patientTabClasses from '../../../patient/tab/PatientTabs.module.css';
import PatientTabButton from '../../../patient/tab/PatientTabButton';
import { useAppointmentsList } from '../../../../lib/hooks';

import classes from './WorkQDrawer.module.css'

import CaseDetailTab from './tabs/CaseDetailTab';
import NotesDrawerTab from './tabs/NotesDrawerTab';
import ServicesDrawerTab from './tabs/ServicesDrawerTab';
import DocumentsDrawerTab from './tabs/DocumentsDrawerTab';
import AttorneyDrawerTab from './tabs/AttorneyDrawerTab';
import LiabilityDrawerTab from './tabs/LiabilityDrawerTab';
import MedPayDrawerTab from './tabs/MedPayDrawerTab';
import UMDrawerTab from './tabs/UMDrawerTab';
import MajorMedDrawerTab from './tabs/MajorMedDrawerTab';
import WorkersCompDrawerTab from './tabs/WorkersCompDrawerTab';
import LienDrawerTab from './tabs/LienDrawerTab';
import NegotiationDrawerTab from './tabs/NegotiationDrawerTab';
import AppointmentsDrawerTab from './tabs/AppointmentsDrawerTab';
import CrimeVictimDrawerTab from './tabs/CrimeVictimDrawerTab';
import SOSDrawerTab from './tabs/SOSDrawerTab';
import ASCDrawerTab from './tabs/ASCDrawerTab';
import SelfPayDrawerTab from './tabs/SelfPayDrawerTab';
import ProcoDocsDrawerTab from './tabs/ProcoDocsDrawerTab';
import TransportationTab from './tabs/TransportationTab';
import FlagsDrawerTab from './tabs/FlagsDrawerTab';
import HistoryDrawerTab from './tabs/HistoryDrawerTab';

const STANDARD_TABS = [
    { id: 'case_details_drawer', label: 'Overview', Panel: CaseDetailTab },
    { id: 'notes', label: 'Notes', Panel: NotesDrawerTab },
    { id: 'services', label: 'Services', Panel: ServicesDrawerTab },
    { id: 'documents', label: 'Documents', Panel: DocumentsDrawerTab },
    { id: 'attorney', label: 'Attorney', Panel: AttorneyDrawerTab },
    { id: 'liability', label: 'Liability', Panel: LiabilityDrawerTab },
    { id: 'medpay_pip', label: 'Medpay/PIP', Panel: MedPayDrawerTab },
    { id: 'um', label: 'UM', Panel: UMDrawerTab },
    { id: 'major_med', label: 'Major Med', Panel: MajorMedDrawerTab },
    { id: 'transportation', label: 'Transportation', Panel: TransportationTab },
    { id: 'workers_comp', label: "Worker's Comp", Panel: WorkersCompDrawerTab },
    { id: 'lien', label: 'Lien', Panel: LienDrawerTab },
    { id: 'negotiation', label: 'Negotiation', Panel: NegotiationDrawerTab },
    { id: 'appointments', label: 'Appointments', Panel: AppointmentsDrawerTab },
    { id: 'flags', label: 'Flags', Panel: FlagsDrawerTab },
    { id: 'history', label: 'History', Panel: HistoryDrawerTab },
    { id: 'crime_victim', label: 'Crime Victim', Panel: CrimeVictimDrawerTab },
    { id: 'sos', label: 'SOS', Panel: SOSDrawerTab },
    { id: 'asc', label: 'ASC', Panel: ASCDrawerTab },
    { id: 'self_pay', label: 'Self Pay', Panel: SelfPayDrawerTab },
    { id: 'proco_docs', label: 'Proco Docs', Panel: ProcoDocsDrawerTab },
    { id: 'negotiation_escalation', label: 'Negotiation Escalation', Panel: NegotiationDrawerTab },
]

/** When present on a work queue, these tabs skip patient-strip (case type + permission) visibility only. */
const WORK_QUEUE_DRAWER_TABS_SKIP_VISIBILITY = new Set(['appointments', 'flags', 'history'])

const TAB_ALIAS = {
    // Legacy API/config ids → values MUST match STANDARD_TABS `id` exactly (keys in `byId`).
    case: 'case_details_drawer',
    overview: 'case_details_drawer',
    case_details: 'case_details_drawer',
    case_detail: 'case_details_drawer',
    major_med: 'major_med',
    major_medical: 'major_med',
    majormed: 'major_med',
    workers_comp: 'workers_comp',
    workerscompensation: 'workers_comp',
    workerscomp: 'workers_comp',
    crime_victim: 'crime_victim',
    crimevictim: 'crime_victim',
    transportation: 'transportation',
    self_pay: 'self_pay',
    selfpay: 'self_pay',
    proco_docs: 'proco_docs',
    procodocs: 'proco_docs',
    pip: 'medpay_pip',
    medpay: 'medpay_pip',
    mp_pip_um: 'medpay_pip',
    negotiation_escalation: 'negotiation_escalation',
    case_flags: 'flags',
    case_flag: 'flags',
    flag: 'flags',
}

function normalizeDrawerTabKey(raw) {
    if (raw == null || raw === '') return ''
    const s = String(raw).trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
    return TAB_ALIAS[s] || s
}

function tabKeyFromWorkQEntry(entry) {
    if (entry == null) return ''
    if (typeof entry === 'string' || typeof entry === 'number') {
        return normalizeDrawerTabKey(entry)
    }
    if (typeof entry === 'object') {
        const raw = entry.id ?? entry.slug ?? entry.tab ?? entry.key ?? entry.name
        return normalizeDrawerTabKey(raw)
    }
    return ''
}

function drawerTabsForWorkQ(workq) {
    const byId = Object.fromEntries(STANDARD_TABS.map((t) => [t.id, t]))
    const config = workq?.tabs
    if (!Array.isArray(config) || config.length === 0) {
        return STANDARD_TABS
    }

    const ordered = []
    for (const entry of config) {
        const key = tabKeyFromWorkQEntry(entry)
        const base = byId[key]
        if (!base) continue
        const labelOverride =
            typeof entry === 'object' && entry && typeof entry.label === 'string' && entry.label.trim() !== ''
                ? entry.label.trim()
                : null
        ordered.push(labelOverride ? { ...base, label: labelOverride } : { ...base })
    }

    // If backend provides an explicit tabs config, do not silently expand to all tabs.
    // Unknown/non-drawer entries (e.g. "transportation") should result in no drawer tabs.
    return ordered
}

function patientTabItemForDrawerTab(drawerTab) {
    const tabKey = DRAWER_TAB_TO_PATIENT_TAB[drawerTab.id] ?? drawerTab.id
    const base = tabs_button.find((b) => b.tab === tabKey)
    if (!base) {
        return {
            id: drawerTab.id,
            name: drawerTab.label,
            tab: tabKey,
            folder: null,
            order: 999,
            permissions: 'skip',
        }
    }
    return { ...base, name: drawerTab.label }
}

/** Survives virtualized WorkQRow remounts so the open drawer keeps the selected tab. */
const drawerTabIndexByCaseKey = new Map()

function drawerCaseTabKey(slug, caseId) {
    if (caseId == null || caseId === '') return null
    return `${slug ?? ''}::${caseId}`
}

export default function WorkQDrawer({ data, slug, workq }) {

    const dispatch = useDispatch()
    const navigate = useNavigate();

    const caseId = data?.case?.id
    const personNumber = data?.patient?.person_number
    const tabCacheKey = drawerCaseTabKey(slug, caseId)

    const [loading, setLoading] = useState(true)
    const [activeTabIndex, setActiveTabIndex] = useState(() => {
        if (tabCacheKey == null) return 0
        const cached = drawerTabIndexByCaseKey.get(tabCacheKey)
        return typeof cached === 'number' && cached >= 0 ? cached : 0
    })
    const [localCaseData, setLocalCaseData] = useState(null)

    const userToken = useSelector(state => state.user.token)
    const user = useSelector(state => state.user.user)
    const activeCase = useSelector(state => state.case.activeCase)
    const activeCaseRef = useRef(activeCase)
    activeCaseRef.current = activeCase

    const drawerFetchCaseIdRef = useRef(null)
    const drawerRequestSeqRef = useRef(0)
    const deferredDispatchRef = useRef(null)
    const prevTabCaseKeyRef = useRef(tabCacheKey)
    // Keep latest row snapshot for merge without putting `data` in the fetch effect deps
    // (row object identity changes often and would re-fetch / flicker the drawer).
    const rowDataRef = useRef(data)
    rowDataRef.current = data

    const localCaseId = localCaseData ? getCaseIdFromCasePayload(localCaseData) : null
    const resolvedStoreCaseId = activeCase ? getCaseIdFromCasePayload(activeCase) : null
    const idMatchesRow =
        caseId != null &&
        resolvedStoreCaseId != null &&
        Number(resolvedStoreCaseId) === Number(caseId)
    const isDrawerCase = caseId != null && activeCase && idMatchesRow

    const localCaseMatchesRow =
        caseId != null &&
        localCaseId != null &&
        Number(localCaseId) === Number(caseId)

    const caseData = localCaseMatchesRow
        ? localCaseData
        : (isDrawerCase ? activeCase : null)

    const drawerTabs = useMemo(() => drawerTabsForWorkQ(workq), [workq])

    const permissionTabs = useMemo(() => {
        const p = viewTabPermission(tabs_button, user?.permissions)
        return Array.isArray(p) ? p : []
    }, [user?.permissions])

    const visiblePatientTabs = useMemo(() => {
        if (!caseData?.case?.case_type_id) {
            return null
        }
        const foundItem = getCaseTypeTabConfig(caseData)
        if (!foundItem) {
            return null
        }
        return computeVisiblePatientTabItems({
            activeCase: caseData,
            permission_tabs: permissionTabs,
            foundItem,
        })
    }, [caseData, permissionTabs])

    const filteredDrawerTabs = useMemo(() => {
        if (workq?.complex_filtering === 'transportation') {
            return drawerTabs
        }
        if (visiblePatientTabs == null) {
            return drawerTabs
        }
        return drawerTabs.filter((t) => {
            if (WORK_QUEUE_DRAWER_TABS_SKIP_VISIBILITY.has(t.id)) {
                return true
            }
            return drawerTabMatchesPatientVisibility(t.id, visiblePatientTabs)
        })
    }, [drawerTabs, visiblePatientTabs, workq?.complex_filtering])

    useEffect(() => {
        const prev = prevTabCaseKeyRef.current
        if (prev != null && tabCacheKey != null && prev !== tabCacheKey) {
            setActiveTabIndex(0)
        }
        prevTabCaseKeyRef.current = tabCacheKey
    }, [tabCacheKey])

    useEffect(() => {
        if (tabCacheKey == null) return
        drawerTabIndexByCaseKey.set(tabCacheKey, activeTabIndex)
    }, [tabCacheKey, activeTabIndex])

    useEffect(() => {
        setActiveTabIndex((i) => {
            if (filteredDrawerTabs.length === 0) {
                return 0
            }
            return i >= filteredDrawerTabs.length ? 0 : i
        })
    }, [filteredDrawerTabs.length])

    function goToCaseTabHandler(tabId) {
        dispatch(workqueueActions.setOpenedDrawer(null))
        navigate(`/work-queues/${slug}/${data.patient.person_number}`, { replace: true })
        if (tabId !== null) {
            dispatch(detailsActions.setActiveTab(tabId))
        }
    }

    const clearDeferredStoreSync = useCallback(() => {
        if (deferredDispatchRef.current != null) {
            clearTimeout(deferredDispatchRef.current)
            deferredDispatchRef.current = null
        }
    }, [])

    // Defer Redux sync one tick so drawer local state / tab UI paint first.
    // ProcoDocs null-guards cover the brief window before activeCase lands.
    const scheduleActiveCaseSync = useCallback((payload) => {
        clearDeferredStoreSync()
        deferredDispatchRef.current = setTimeout(() => {
            dispatch(caseActions.setActiveCase(payload))
            deferredDispatchRef.current = null
        }, 0)
    }, [clearDeferredStoreSync, dispatch])

    useEffect(() => {
        if (!caseId || !personNumber || !userToken) {
            drawerFetchCaseIdRef.current = null
            clearDeferredStoreSync()
            setLocalCaseData(null)
            setLoading(false)
            return
        }

        drawerFetchCaseIdRef.current = null
        setLocalCaseData(null)
        setLoading(true)
        const requestSeq = ++drawerRequestSeqRef.current

        async function getData() {
            try {
                const drawerD = await getWorkQDrwaer(userToken, slug, personNumber, true)
                if (requestSeq !== drawerRequestSeqRef.current) {
                    return
                }
                if (drawerD?.errorMessage) {
                    return
                }
                const rowData = rowDataRef.current
                let drawerPayload = drawerD
                if (rowData && typeof rowData === 'object') {
                    // Drawer endpoint returns a slice only (no `case` / `patient`); merge row data
                    // so id matching and tab panels see a full case shape.
                    drawerPayload = { ...rowData, ...drawerPayload }
                }
                if (
                    drawerPayload &&
                    rowData?.case_flag != null &&
                    (drawerPayload.case_flag === undefined || drawerPayload.case_flag === null)
                ) {
                    drawerPayload = { ...drawerPayload, case_flag: rowData.case_flag }
                }
                const normalized = normalizeActiveCaseShape(recalculateCaseCounters(drawerPayload))
                drawerFetchCaseIdRef.current = caseId
                setLocalCaseData(normalized)
                scheduleActiveCaseSync(normalized)
            } finally {
                if (requestSeq === drawerRequestSeqRef.current) {
                    setLoading(false)
                }
            }
        }

        getData()
        return () => {
            drawerFetchCaseIdRef.current = null
            clearDeferredStoreSync()
            if (requestSeq === drawerRequestSeqRef.current) {
                setLoading(false)
            }
        }
        // Intentionally omit `data`: fetch only when case/person/slug/token change.
    }, [caseId, personNumber, slug, userToken, dispatch, clearDeferredStoreSync, scheduleActiveCaseSync])

    useEffect(() => {
        if (!caseId) return

        const eventName = `case-updated-${caseId}`

        const caseUpdatedHandler = (incoming) => {
            const current = activeCaseRef.current
            const currentId = getCaseIdFromCasePayload(current)
            if (current != null && currentId != null && Number(currentId) !== Number(caseId)) {
                return
            }
            const cleanedIncoming = sanitizeCaseUpdatePayload(incoming)
            if (Object.keys(cleanedIncoming).length === 0) {
                return
            }
            const merged = normalizeActiveCaseShape(mergeCaseUpdateData(current || {}, incoming))
            dispatch(caseActions.setActiveCase(merged))
            applyDeletedNotesPatch(dispatch, cleanedIncoming?.notes, caseId)
        }

        if (!socket.connected) {
            socket.connect()
        }

        socket.on(eventName, caseUpdatedHandler)

        return () => {
            socket.off(eventName, caseUpdatedHandler)
        }
    }, [caseId, dispatch])

    const inWorkQ = caseData ?? data
    const activeDrawerTabs = filteredDrawerTabs.length > 0 ? filteredDrawerTabs : drawerTabs
    const activeDrawerTabKey = activeDrawerTabs[activeTabIndex]?.id ?? null
    const contextCaseId = caseData ? getCaseIdFromCasePayload(caseData) : caseId ?? null

    const renderTabPanel = () => {
        if (!caseData) return null

        const tab = activeDrawerTabs[activeTabIndex]
        if (!tab) return null

        const Panel = tab.Panel
        const shared = {
            classes,
            goToCaseTabHandler,
            column: 5,
            inWorkQ,
        }

        if (Panel === CaseDetailTab) {
            return (
                <Panel
                    {...shared}
                    data={data}
                    caseData={caseData}
                />
            )
        }

        return <Panel {...shared} />
    }

    const shouldShowLoader = loading && !caseData

    const workQueueAppointmentsListEnabled =
        !shouldShowLoader &&
        activeDrawerTabKey === 'appointments' &&
        contextCaseId != null

    useAppointmentsList(workQueueAppointmentsListEnabled ? contextCaseId : null, {
        enabled: workQueueAppointmentsListEnabled,
    })

    return (
        <WorkQueueUpdateContext.Provider
            value={{
                slug: slug ?? null,
                caseId: contextCaseId,
                activeTabKey: activeDrawerTabKey,
            }}
        >
            <div className={classes.container}>
                {!shouldShowLoader && activeDrawerTabs.length > 1 && (
                    <div className={patientTabClasses.tabs_container} role="tablist">
                        <div className={patientTabClasses.tabs_container_wrapper}>
                            {activeDrawerTabs.map((tab, index) => (
                                <PatientTabButton
                                    key={tab.id}
                                    item={patientTabItemForDrawerTab(tab)}
                                    clickAction={() => {}}
                                    disabled={shouldShowLoader}
                                    drawerClickOverride={() => {
                                        setActiveTabIndex(index)
                                    }}
                                    activeTabOverride={activeTabIndex === index}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <div className={classes.tabPanel} role="tabpanel">
                    {shouldShowLoader ? <LoadingSpinner /> : (renderTabPanel() ?? <LoadingSpinner />)}
                </div>
            </div>
        </WorkQueueUpdateContext.Provider>
    )
}
